import React, { useMemo, useState } from 'react';
import { Box, Button, ColumnLayout, Container, Pagination } from '@amzn/awsui-components-react';
import { DefaultColumnFilter, TPEReactTable } from 'src/components/shared/TPEReactTable';

export const SELECTED_FOR_PROMOTION_PAGE_SIZE = 5;

export interface SelectedForPromotionColumn {
    accessor: string;
    header: string;
}

interface SelectedForPromotionTableProps {
    /** Ordered selected rows: promotion row key -> row snapshot (taken at selection time). */
    selectedRows: Map<string, any>;
    /** Columns to display (same accessors as the main grid, minus checkbox/error columns). */
    columns: SelectedForPromotionColumn[];
    /** Removes one row from the selection. */
    onRemove: (rowKey: string) => void;
    /** Disables the remove buttons (e.g. while a promotion is in flight). */
    removeDisabled?: boolean;
    className?: string;
}

/**
 * Read-only "Selected For Promotion" table shown above the CDT / Custom COA grids. Lists the
 * rows currently selected for promotion with local sort, per-column search, and pagination;
 * each row has a remove (x) button. Renders nothing when the selection is empty.
 */
export function SelectedForPromotionTable(props: SelectedForPromotionTableProps) {
    const { selectedRows, columns, onRemove, removeDisabled = false, className = '' } = props;

    const [showFilters, setShowFilters] = useState(false);
    const [currentPageIndex, setCurrentPageIndex] = useState(1);
    const [filters, setFilters] = useState<Array<{ id: string, value: string }>>([]);
    const [tableKey, setTableKey] = useState(0);

    const totalCount = selectedRows.size;

    const visibleRows = useMemo(() => {
        let rows = Array.from(selectedRows.entries()).map(([rowKey, row]) => ({ ...row, __promotionRowKey: rowKey }));
        filters.forEach(f => {
            if (f.value == null || f.value === '') {
                return;
            }
            rows = rows.filter(r => String(r[f.id] ?? '').includes(f.value));
        });
        return rows;
    }, [selectedRows, filters]);

    const pagesCount = Math.max(1, Math.ceil(visibleRows.length / SELECTED_FOR_PROMOTION_PAGE_SIZE));
    // Filtering/removal can shrink the row set below the current page — show the last page then.
    const effectivePageIndex = Math.min(currentPageIndex, pagesCount);
    const pagedRows = useMemo(
        () => visibleRows.slice(
            (effectivePageIndex - 1) * SELECTED_FOR_PROMOTION_PAGE_SIZE,
            effectivePageIndex * SELECTED_FOR_PROMOTION_PAGE_SIZE
        ),
        [visibleRows, effectivePageIndex]
    );

    const colDefs = useMemo(() => {
        const removeColumn = {
            accessor: '__remove',
            Header: (e: any) => <div className="cell-text"> </div>,
            sortable: false,
            suppressSorting: true,
            disableFilters: true,
            suppressFiltering: true,
            width: 40,
            Cell: (e: any) => (
                <div className="cell-text">
                    <Button
                        variant="icon"
                        iconName="close"
                        ariaLabel="Remove from promotion"
                        disabled={removeDisabled}
                        onClick={() => onRemove(e.row.original.__promotionRowKey)}
                    />
                </div>
            ),
        };
        const dataColumns = columns.map(col => ({
            accessor: col.accessor,
            Header: (e: any) => <div className="cell-text">{col.header}</div>,
            Cell: (e: any) => <div className="textCell">{e.value == null || e.value === '' ? 'N/A' : String(e.value)}</div>,
            Filter: DefaultColumnFilter,
            hidePopover: true,
            sortable: false,
            suppressSorting: true,
        }));
        return [removeColumn, ...dataColumns];
    }, [columns, onRemove, removeDisabled]);

    if (totalCount === 0) {
        return null;
    }

    return (
        <Container data-class="containerWithHeaderAndGrid">
            <div>
                <div className="customTableTableHeader">
                    <ColumnLayout columns={2} data-class="fullColumnLayout">
                        <Box variant="h2">
                            <span>Selected For Promotion </span>
                            <span className="customTablesRecordCounter">({totalCount})</span>
                        </Box>
                        <Box float="right" />
                        <Button
                            className={showFilters ? 'buttonIconActive' : 'buttonIconInactive'}
                            iconAlign="left"
                            iconName="filter"
                            onClick={() => {
                                if (showFilters) {
                                    // Remount the table to clear its filter inputs.
                                    setFilters([]);
                                    setTableKey(prev => prev + 1);
                                }
                                setShowFilters(!showFilters);
                            }}
                        >Filter rows</Button>
                        {pagesCount > 1 &&
                            <Box float="right">
                                <Pagination
                                    currentPageIndex={effectivePageIndex}
                                    pagesCount={pagesCount}
                                    onChange={({ detail }) => setCurrentPageIndex(detail.currentPageIndex)}
                                />
                            </Box>
                        }
                    </ColumnLayout>
                </div>
                <div className="tableContainer selectedForPromotionTableContainer">
                    <div className={`selectedForPromotionGrid ${className}`} style={removeDisabled ? { opacity: 0.7 } : undefined}>
                        <TPEReactTable {...{
                            data: pagedRows,
                            columnDefinitions: colDefs,
                            className: 'nonGridTable selectedForPromotionTable',
                            key: tableKey,
                            sortable: false,
                            showFilters: showFilters,
                            highlightErrorCells: false,
                            onFilter: setFilters,
                            // Data is re-derived from this table's own filter callback — don't let
                            // the resulting data-identity change reset that state.
                            preserveStateOnDataChange: true,
                            editableOptions: null as unknown as any,
                        }} />
                    </div>
                </div>
            </div>
        </Container>
    );
}
